import { useRouter } from "@tanstack/react-router";
import { useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useRef, type TransitionEvent } from "react";

import type { SidebarDestination } from "./useSidebarPageCache";

const matterDetailPath =
  /^\/matters\/(?!settings(?:\/|$)|my-matters(?:\/|$))[^/]+(?:\/|$)/;

export function useSidebarNavigation({
  panelOpen,
  isMobile,
  scope,
  onCancel,
}: {
  panelOpen: boolean;
  isMobile: boolean;
  scope: string;
  onCancel: () => void;
}) {
  const router = useRouter();
  const reduceMotion = useReducedMotion();
  const pending = useRef<{
    destination: SidebarDestination;
    timeout: ReturnType<typeof setTimeout>;
  } | null>(null);
  const paintFrame = useRef<number | null>(null);

  const clearPending = useCallback(() => {
    if (!pending.current) return false;
    clearTimeout(pending.current.timeout);
    if (paintFrame.current !== null) cancelAnimationFrame(paintFrame.current);
    paintFrame.current = null;
    pending.current = null;
    return true;
  }, []);

  const cancel = useCallback(() => {
    if (clearPending()) onCancel();
  }, [clearPending, onCancel]);

  const navigate = useCallback(() => {
    const next = pending.current;
    clearPending();
    if (next) void router.navigate(next.destination);
  }, [clearPending, router]);

  const finishExpansion = useCallback(() => {
    if (!pending.current || paintFrame.current !== null) return;
    clearTimeout(pending.current.timeout);
    // transitionend runs before paint; let the expanded panel paint before mounting.
    paintFrame.current = requestAnimationFrame(() => {
      paintFrame.current = requestAnimationFrame(navigate);
    });
  }, [navigate]);

  useEffect(
    () => router.subscribe("onBeforeNavigate", cancel),
    [router, cancel],
  );
  useEffect(() => {
    if (!panelOpen || isMobile) cancel();
    else if (reduceMotion) navigate();
  }, [panelOpen, isMobile, reduceMotion, cancel, navigate]);
  useEffect(
    () => () => {
      clearPending();
    },
    [scope, clearPending],
  );

  const deferNavigation = (destination?: SidebarDestination) => {
    const wasWaiting = Boolean(pending.current);
    cancel();
    if (
      !destination ||
      isMobile ||
      reduceMotion ||
      (panelOpen && !wasWaiting) ||
      !matterDetailPath.test(destination.to)
    )
      return false;

    // Mount the cached matter after expansion, rather than during its frames.
    // The fallback covers a missing transitionend (for example, a hidden tab).
    pending.current = {
      destination,
      timeout: setTimeout(finishExpansion, 250),
    };
    return true;
  };

  const onTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === "width"
    ) {
      finishExpansion();
    }
  };

  return { deferNavigation, onTransitionEnd };
}
